import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { AttendanceSource, MeetingAttendanceStatus } from 'app/types/uconnect';
import { FetchedMeeting } from './types';

export const MANUAL_ATTENDANCE_SOURCES: AttendanceSource[] = [
  AttendanceSource.ManualFacilitator,
  AttendanceSource.ManualStaffOverride,
];

export function isManualAttendanceSource(source: AttendanceSource | null | undefined): boolean {
  return !!source && MANUAL_ATTENDANCE_SOURCES.includes(source);
}

export function downloadCsvUrl(url: string, filename?: string) {
  const link = document.createElement('a');
  link.href = url;
  filename && link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
}

export function downloadCsvString(data: string, filename: string) {
  const url = window.URL.createObjectURL(new Blob([data]));
  downloadCsvUrl(url, filename);
}

export function getExcuseRequestCount(meeting: FetchedMeeting) {
  return meeting.meetingUsers.meetingUsers.filter((u) => {
    return u.attendanceRequest?.createdAt && !u.attendanceRequest.judgeUserKey;
  }).length;
}

export function getAttendedCount(meeting: FetchedMeeting) {
  return meeting.meetingUsers.meetingUsers.filter((u) => u.attendance?.status === MeetingAttendanceStatus.Attended)
    .length;
}

export function hasManualEdits(meeting: FetchedMeeting) {
  return meeting.meetingUsers.meetingUsers.some((u) => isManualAttendanceSource(u.attendanceSource));
}

export function isPastMeeting(meeting: FetchedMeeting) {
  return dayjs(meeting.endAt).isBefore(dayjs());
}

export function isUpcomingMeeting(meeting: FetchedMeeting) {
  return dayjs(meeting.startAt).isAfter(dayjs());
}

export function isMeetingInSession(meeting: FetchedMeeting) {
  return !isPastMeeting(meeting) && !isUpcomingMeeting(meeting);
}

// Maps raw attendance-update API errors to clear, user-facing copy. Falls back
// to a generic message so we never surface raw server strings to mentors.
export function getAttendanceErrorMessage(error: unknown, t: TFunction): string {
  const rawMessage = (error as { data?: string })?.data || '';

  if (/EXCUSED for future meeting/i.test(rawMessage)) {
    return t('connect.errors.futureMeetingExcusedOnly');
  }

  return t('connect.errors.attendanceUpdateFailed');
}

// Zoom sync errors that mean there is nothing to sync (yet) rather than a
// failure, keyed to the copy shown when a facilitator syncs by hand. The sync
// that runs on page load stays quiet for these.
const ZOOM_SYNC_GATE_ERRORS: Array<{ pattern: RegExp; messageKey: string }> = [
  { pattern: /meeting has not ended/i, messageKey: 'connect.zoomSync.errors.notEnded' },
  { pattern: /meeting has no zoom link/i, messageKey: 'connect.zoomSync.errors.noZoomLink' },
  { pattern: /no matching zoom occurrence/i, messageKey: 'connect.zoomSync.errors.noOccurrence' },
  // Zoom answers 404 for the participant list while it is still processing a
  // meeting that just ended. A 404 from any other step is a real failure.
  {
    pattern: /listing participants for occurrence .*zoom API returned status 404(?!\d)/i,
    messageKey: 'connect.zoomSync.errors.noOccurrence',
  },
  { pattern: /zoom service is not configured/i, messageKey: 'connect.zoomSync.errors.failed' },
];

function findZoomSyncGateError(error: unknown) {
  const rawMessage = (error as { data?: unknown })?.data;
  if (typeof rawMessage !== 'string') return undefined;
  return ZOOM_SYNC_GATE_ERRORS.find(({ pattern }) => pattern.test(rawMessage));
}

export function isZoomSyncGateError(error: unknown): boolean {
  return !!findZoomSyncGateError(error);
}

export function getZoomSyncErrorMessage(error: unknown, t: TFunction): string {
  return t(findZoomSyncGateError(error)?.messageKey ?? 'connect.zoomSync.errors.failed');
}
